import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

class NumerologyTradingClient {
  constructor() {
    this.baseURL = `${API_BASE_URL}/numerology`;
  }

  async createUserProfile(profileData) {
    try {
      const response = await axios.post(`${this.baseURL}/create-profile`, profileData);
      return response.data;
    } catch (error) {
      console.error('Error creating user profile:', error);
      throw new Error(error.response?.data?.detail || 'Failed to create user profile');
    }
  }

  async createStockProfile(stockData) {
    try {
      const response = await axios.post(`${this.baseURL}/create-stock-profile`, stockData);
      return response.data;
    } catch (error) {
      console.error('Error creating stock profile:', error);
      throw new Error(error.response?.data?.detail || 'Failed to create stock profile');
    }
  }

  async getTradingSignals(signalData) {
    try {
      const response = await axios.post(`${this.baseURL}/trading-signals`, signalData);
      return response.data;
    } catch (error) {
      console.error('Error getting trading signals:', error);
      throw new Error(error.response?.data?.detail || 'Failed to get trading signals');
    }
  }

  async getPortfolioAnalysis(portfolioData) {
    try {
      const response = await axios.post(`${this.baseURL}/portfolio-analysis`, portfolioData);
      return response.data;
    } catch (error) {
      console.error('Error getting portfolio analysis:', error);
      throw new Error(error.response?.data?.detail || 'Failed to get portfolio analysis');
    }
  }

  async getUserProfile(userId) {
    try {
      const response = await axios.get(`${this.baseURL}/user-profile/${userId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting user profile:', error);
      throw new Error(error.response?.data?.detail || 'Failed to get user profile');
    }
  }

  async getStockProfile(symbol) {
    try {
      const response = await axios.get(`${this.baseURL}/stock-profile/${symbol}`);
      return response.data;
    } catch (error) {
      console.error('Error getting stock profile:', error);
      throw new Error(error.response?.data?.detail || 'Failed to get stock profile');
    }
  }

  async getCompatibility(userId, symbol) {
    try {
      const response = await axios.get(`${this.baseURL}/compatibility/${userId}/${symbol}`);
      return response.data;
    } catch (error) {
      console.error('Error getting compatibility:', error);
      throw new Error(error.response?.data?.detail || 'Failed to get compatibility');
    }
  }

  async getBulkAnalysis(userId) {
    try {
      const response = await axios.post(`${this.baseURL}/bulk-analysis/${userId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting bulk analysis:', error);
      throw new Error(error.response?.data?.detail || 'Failed to get bulk analysis');
    }
  }

  async getBuySignalsOnly(userId, batchSize = 50, minConfidence = 0.6) {
    try {
      const response = await axios.post(`${this.baseURL}/bulk-analysis-buy-only/${userId}?batch_size=${batchSize}&min_confidence=${minConfidence}`);
      return response.data;
    } catch (error) {
      console.error('Error getting BUY signals only:', error);
      throw new Error(error.response?.data?.detail || 'Failed to get BUY signals only');
    }
  }

  async getTopSymbols(count = 50) {
    try {
      const response = await axios.get(`${this.baseURL}/top-symbols?count=${count}`);
      return response.data;
    } catch (error) {
      console.error('Error getting top symbols:', error);
      throw new Error(error.response?.data?.detail || 'Failed to get top symbols');
    }
  }

  async streamBulkAnalysis(userId, onProgress, batchSize = 50) {
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    const url = `${API_BASE_URL}/numerology/bulk-analysis-stream/${userId}?batch_size=${batchSize}`;
    
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              onProgress(data);
              
              if (data.type === 'complete' || data.type === 'error') {
                return data;
              }
            } catch (e) {
              console.error('Error parsing SSE data:', e);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error in streaming bulk analysis:', error);
      throw new Error('Failed to stream bulk analysis: ' + error.message);
    }
  }

  async healthCheck() {
    try {
      const response = await axios.get(`${this.baseURL}/health`);
      return response.data;
    } catch (error) {
      console.error('Error checking health:', error);
      throw new Error(error.response?.data?.detail || 'Failed to check health');
    }
  }
}

export const numerologyTradingClient = new NumerologyTradingClient();
